(function (global, factory) {
  typeof exports === 'object' && typeof module !== 'undefined' ? factory(exports) :
  typeof define === 'function' && define.amd ? define(['exports'], factory) :
  (global = global || self, factory(global.agos = {}));
}(this, (function (exports) { 'use strict';

  const noop = () => {};

  const pipe = (...cbs) => {
    let res = cbs[0];
    for (let i = 1, n = cbs.length; i < n; i++) res = cbs[i](res);
    return res;
  };

  const IDLE = 0;
  const ACTIVE = 1;
  const DONE = 2;

  class Observable {
    constructor(source) {
      this.source = source;
    }

    listen(open, next, fail, done, talkback) {
      const cancel = TalkbackCancelInterceptor.join(talkback);
      let state = IDLE;
      this.source(
        () => {
          if (state === IDLE) {
            state = ACTIVE;
            open();
          }
        },
        value => {
          if (state === ACTIVE) {
            try {
              next(value);
            } catch (error) {
              fail(error);
            }
          }
        },
        error => {
          if (state === ACTIVE) fail(error);
        },
        cancelled => {
          if (state === ACTIVE) {
            cancel.run();
            done(cancelled);
            state = DONE;
          }
        },
        cancel
      );
    }
  }

  Observable.CANCEL = Symbol("cancel");

  class Operator extends Observable {
    static join(observable, ...args) {
      return new this((open, next, fail, done, talkback) => {
        observable.listen(open, next, fail, done, talkback);
      }, ...args);
    }
  }

  class CancelInterceptor extends Operator {
    constructor(source) {
      super(source);
      this.run = noop;
    }

    listen(open, next, fail, done, talkback) {
      this.run = () => next([Observable.CANCEL]);
      this.source(open, next, fail, done, talkback);
    }
  }

  class TalkbackCancelInterceptor extends Operator {
    constructor(source) {
      super(source);
      this.run = noop;
    }

    listen(open, next, fail, done, talkback) {
      const cancel = CancelInterceptor.join(talkback);
      this.run = () => cancel.run();
      this.source(open, next, fail, done, cancel);
    }
  }

  class Map extends Operator {
    constructor(source, project) {
      super(source);
      this.project = project;
    }

    static join(observable, project) {
      return observable instanceof Map
        ? new Map(observable.source, value => project(observable.project(value)))
        : super.join(observable, project);
    }

    listen(open, next, fail, done, talkback) {
      this.source(open, value => next(this.project(value)), fail, done, talkback);
    }
  }

  const map = project => observable => Map.join(observable, project);

  const tap = project =>
    map(value => {
      project(value);
      return value;
    });

  const create = source => new Observable(source);

  const never = () => create(noop);

  class Chain extends Operator {
    constructor(source, projects) {
      super(source);
      this.projects = projects;
    }

    static join(observable, projects) {
      return observable instanceof Chain
        ? new Chain(observable.source, [...observable.projects, ...projects])
        : super.join(observable, projects);
    }

    listen(open, next, fail, done, talkback) {
      const cancels = [];
      let cancelled = false;
      let active = 0;

      const run = index => value => {
        const project = this.projects[index];
        const observable = project(value);
        const cancel = CancelInterceptor.join(never());
        cancels.push(cancel);

        observable.listen(
          () => active++,
          index >= this.projects.length - 1 ? next : run(index + 1),
          fail,
          () => --active <= 0 && done(cancelled),
          cancel
        );
      };

      this.source(
        open,
        this.projects.length === 0 ? next : run(0),
        fail,
        () => active <= 0 && done(cancelled),
        tap(payload => {
          if (payload[0] === Observable.CANCEL) {
            cancelled = true;
            for (let index = 0; index < cancels.length; index++) {
              const cancel = cancels[index];
              cancel.run();
            }
          }
        })(talkback)
      );
    }
  }

  const chain = (...projects) => observable => Chain.join(observable, projects);

  const IDLE$1 = 0;
  const ACTIVE$1 = 1;
  const DONE$1 = 2;

  const emitter = ({ immediate = false } = {}) => {
    let state = IDLE$1;
    let last = null;
    const opens = new Set();
    const nexts = new Set();
    const fails = new Set();
    const dones = new Set();

    const open = () => {
      if (state === IDLE$1) {
        state = ACTIVE$1;
        for (const open of opens.values()) open();
        opens.clear();
      }
    };

    const next = value => {
      last = value;
      if (state === ACTIVE$1) for (const next of nexts.values()) next(value);
    };

    const fail = error => {
      if (state === ACTIVE$1) for (const fail of fails.values()) fail(error);
    };

    const done = cancelled => {
      if (state === ACTIVE$1) {
        state = DONE$1;
        for (const done of dones.values()) done(cancelled);
        nexts.clear();
        fails.clear();
        dones.clear();
      }
    };

    const observable = create((open, next, fail, done, talkback) => {
      if (state === IDLE$1) opens.add(open);
      else if (state === ACTIVE$1) {
        open();
        if (immediate) next(last);
      }

      nexts.add(next);
      fails.add(fail);
      dones.add(done);
      talkback.listen(
        noop,
        payload => {
          if (payload[0] === Observable.CANCEL) {
            nexts.delete(next);
            fails.delete(fail);
            dones.delete(done);
            done(true);
          }
        },
        noop,
        noop,
        never()
      );
    });

    return [
      {
        open,
        next,
        fail,
        done
      },
      observable
    ];
  };

  class Collect extends Operator {
    constructor(source, pipes) {
      super(source);
      this.pipes = pipes;
    }

    listen(open, next, fail, done, talkback) {
      // TO DO investigate if emitter
      // is appropriate to use in collection
      const [controller, subject] = emitter();
      const cancel = CancelInterceptor.join(talkback);
      let cancelled = 0;
      let active = false;
      for (let index = 0; index < this.pipes.length; index++) {
        const pipe = this.pipes[index](subject);
        pipe.listen(
          noop,
          value => next([value, index]),
          error => fail([error, index]),
          () => {
            if (active && ++cancelled >= this.pipes.length) cancel.run();
          },
          never()
        );
      }
      this.source(
        () => {
          active = true;
          open();
          controller.open();
        },
        controller.next,
        controller.fail,
        cancelled => {
          active = false;
          done(cancelled);
          controller.done(cancelled);
        },
        cancel
      );
    }
  }

  const collect = pipes => observable => Collect.join(observable, pipes);

  class Scan extends Operator {
    constructor(source, accumulator, seed) {
      super(source);
      this.accumulator = accumulator;
      this.seed = seed;
    }

    listen(open, next, fail, done, talkback) {
      let seed = this.seed;
      this.source(
        open,
        value => {
          seed = this.accumulator(seed, value);
          next(seed);
        },
        fail,
        done,
        talkback
      );
    }
  }

  const scan = (accumulator, seed) => source =>
    Scan.join(source, accumulator, seed);

  class Filter extends Operator {
    constructor(source, predicate) {
      super(source);
      this.predicate = predicate;
    }

    static join(observable, predicate) {
      return observable instanceof Filter
        ? new Filter(
            observable.source,
            value => observable.predicate(value) && predicate(value)
          )
        : super.join(observable, predicate);
    }

    listen(open, next, fail, done, talkback) {
      this.source(
        open,
        value => this.predicate(value) && next(value),
        fail,
        done,
        talkback
      );
    }
  }

  const filter = predicate => observable => Filter.join(observable, predicate);

  const collectLatest = pipes => observable =>
    filter(values => values.length >= pipes.length)(
      scan((values, [value, index]) => {
        const newValues = [...values];
        newValues[index] = value;
        return newValues;
      }, [])(collect(pipes)(observable))
    );

  const concat = observables =>
    create((open, next, fail, done, talkback) => {
      const cancel = CancelInterceptor.join(never());

      talkback.listen(
        noop,
        payload => {
          if (payload[0] === Observable.CANCEL) {
            cancel.run();
            done(true);
          }
        },
        noop,
        noop,
        never()
      );

      const run = index => {
        const observable = observables[index];
        observable.listen(
          index === 0 ? open : noop,
          next,
          fail,
          () => {
            if (index >= observables.length - 1) return done(false);
            run(index + 1);
          },
          cancel
        );
      };

      run(0);
    });

  const empty = () =>
    create((open, next, fail, done, talkback) => {
      talkback.listen(
        noop,
        payload => payload[0] === Observable.CANCEL && done(true),
        noop,
        noop,
        never()
      );
      open();
      done(false);
    });

  const fromArray = array =>
    create((open, next, fail, done, talkback) => {
      let cancelled = false;
      talkback.listen(
        noop,
        payload => {
          if (payload[0] === Observable.CANCEL) {
            cancelled = true;
            done(true);
          }
        },
        noop,
        noop,
        never()
      );
      open();
      for (let index = 0; index < array.length; index++) {
        if (cancelled) break;
        next([array[index], index]);
      }
      done(false);
    });

  const fromObject = object =>
    create((open, next, fail, done, talkback) => {
      let cancelled = false;
      talkback.listen(
        noop,
        payload => {
          if (payload[0] === Observable.CANCEL) {
            cancelled = true;
            done(true);
          }
        },
        noop,
        noop,
        never()
      );
      open();
      const keys = Object.keys(object);
      for (let index = 0; index < keys.length; index++) {
        if (cancelled) break;
        const key = keys[index];
        const value = object[key];
        next([value, key]);
      }
      done(false);
    });

  const fromPromise = promise =>
    create((open, next, fail, done, talkback) => {
      open();
      promise
        .then(value => next(value))
        .catch(error => fail(error))
        .finally(() => done(false));
      talkback.listen(
        noop,
        payload => payload[0] === Observable.CANCEL && done(true),
        noop,
        noop,
        never()
      );
    });

  const join = () => chain(observable => observable);

  class Slice extends Operator {
    constructor(source, start = 0, end = Infinity) {
      super(source);
      this.start = start;
      this.end = end;
    }

    static join(observable, start = 0, end = Infinity) {
      return observable instanceof Slice
        ? new Slice(
            observable.source,
            start < 0 ? start : observable.start + start,
            end < observable.end ? end : observable.end
          )
        : super.join(observable, start, end);
    }

    listen(open, next, fail, done, talkback) {
      if (this.start < 0 || this.end < 0) {
        // future sink
        const values = [];

        this.source(
          open,
          value => values.push(value),
          fail,
          cancelled => {
            const sliced = values.slice(this.start, this.end);
            for (let index = 0; index < sliced.length; index++) {
              next(sliced[index]);
            }
            done(cancelled);
          },
          talkback
        );
      } else {
        // slice sink
        let count = 0;
        const cancel = CancelInterceptor.join(talkback);

        this.source(
          open,
          value => {
            count++;
            count > this.start && next(value);
            count >= this.end && cancel.run();
          },
          fail,
          done,
          cancel
        );
      }
    }
  }

  const slice = (start, end) => source => Slice.join(source, start, end);

  const last = () => slice(-1);

  const listen = (
    open = noop,
    next = noop,
    fail = noop,
    done = noop,
    talkback = never()
  ) => observable => observable.listen(open, next, fail, done, talkback);

  const merge = (observables, withIndex = false) =>
    create((open, next, fail, done, talkback) => {
      const closed = [];
      const cancels = [];
      talkback.listen(
        noop,
        payload => {
          if (payload[0] === Observable.CANCEL) {
            for (let index = 0; index < cancels.length; index++) {
              const cancel = cancels[index];
              cancel.run();
            }
            done(true);
          }
        },
        noop,
        noop,
        never()
      );
      for (let index = 0; index < observables.length; index++) {
        const observable = observables[index];
        const cancel = CancelInterceptor.join(never());
        cancels[index] = cancel;
        closed[index] = false;
        observable.listen(
          open,
          value => next(withIndex ? [value, index] : value),
          error => fail(withIndex ? [error, index] : error),
          () => {
            closed[index] = true;
            if (closed.length !== observables.length) return;
            let allClosed = true;
            for (let index = 0; index < closed.length; index++) {
              if (!closed[index]) {
                allClosed = false;
                break;
              }
            }
            if (allClosed) done(false);
          },
          cancel
        );
      }
    });

  const mergeLatest = observables =>
    pipe(
      merge(observables, true),
      scan((values, [value, index]) => {
        values[index] = value;
        return values;
      }, []),
      filter(values => values.length >= observables.length)
    );

  const multicast = observable => {
    let active = false;
    const [controller, subject] = emitter();

    return create((open, next, fail, done, talkback) => {
      subject.listen(open, next, fail, done, talkback);
      if (!active) {
        active = true;
        observable.listen(
          controller.open,
          controller.next,
          controller.fail,
          controller.done,
          never()
        );
      }
    });
  };

  const of = value =>
    create((open, next, fail, done, talkback) => {
      talkback.listen(
        noop,
        payload => payload[0] === Observable.CANCEL && done(true),
        noop,
        noop,
        never()
      );
      open();
      next(value);
      done(false);
    });

  const fail = error =>
    create((open, next, fail, done, talkback) => {
      talkback.listen(
        noop,
        payload => payload[0] === Observable.CANCEL && done(true),
        noop,
        noop,
        never()
      );
      open();
      fail(error);
      done(false);
    });

  const skip = amount => slice(amount);

  const skipWhile = predicate => filter(value => !predicate(value));

  const subscribe = (sink, talkback) => {
    if (typeof sink === "function")
      return listen(noop, sink, noop, noop, talkback);
    return listen(sink.open, sink.next, sink.fail, sink.done, talkback);
  };

  class SwitchMap extends Operator {
    constructor(source, projects) {
      super(source);
      this.projects = projects;
    }

    static join(observable, projects) {
      return observable instanceof SwitchMap
        ? new SwitchMap(observable.source, [...observable.projects, ...projects])
        : super.join(observable, projects);
    }

    listen(open, next, fail, done, talkback) {
      const cancels = [];
      let cancelled = false;
      let finished = false;
      let active = 0;

      const run = index => value => {
        cancels[index] = cancels[index] || CancelInterceptor.join(never());
        const cancel = cancels[index];
        cancel.run();
        const project = this.projects[index];
        const observable = project(value);

        observable.listen(
          () => active++,
          index >= this.projects.length - 1 ? next : run(index + 1),
          fail,
          () => --active <= 0 && finished && done(cancelled),
          cancel
        );
      };

      this.source(
        open,
        this.projects.length === 0 ? next : run(0),
        fail,
        cancel => {
          finished = true;
          cancelled = cancel;
          if (active <= 0) done(cancelled);
        },
        tap(payload => {
          if (payload[0] === Observable.CANCEL) {
            for (let index = 0; index < cancels.length; index++) {
              const cancel = cancels[index];
              cancel.run();
            }
          }
        })(talkback)
      );
    }
  }

  const switchMap = project => observable =>
    SwitchMap.join(observable, [project]);

  const take = amount => slice(0, amount);

  class TakeWhile extends Operator {
    constructor(source, predicate) {
      super(source);
      this.predicate = predicate;
    }

    static join(observable, predicate) {
      return observable instanceof TakeWhile
        ? new TakeWhile(observable.source, value => {
            const [included, inclusive] = observable.predicate(value);
            if (!included) return [included, inclusive];
            return predicate(value);
          })
        : super.join(observable, predicate);
    }

    listen(open, next, fail, done, talkback) {
      const cancel = CancelInterceptor.join(talkback);

      this.source(
        open,
        value => {
          const [included, inclusive] = this.predicate(value);
          if (!included) {
            if (inclusive) next(value);
            return cancel.run();
          }
          next(value);
        },
        fail,
        done,
        cancel
      );
    }
  }

  const takeWhile = (predicate, inclusive = false) => observable =>
    TakeWhile.join(observable, value => [predicate(value), inclusive]);

  class Throttle extends Operator {
    constructor(source, tick) {
      super(source);
      this.tick = tick;
    }

    static join(observable, tick) {
      return observable instanceof Throttle
        ? new Throttle(observable.source, (release, data) =>
            observable.tick(() => tick(release, data), data)
          )
        : super.join(observable, tick);
    }

    listen(open, next, fail, done, talkback) {
      let active = false;
      this.source(
        open,
        value => {
          if (active) return;
          active = true;
          this.tick(() => {
            next(value);
            active = false;
          }, value);
        },
        fail,
        done,
        talkback
      );
    }
  }

  const throttle = tick => observable => Throttle.join(observable, tick);

  exports.CancelInterceptor = CancelInterceptor;
  exports.Observable = Observable;
  exports.Operator = Operator;
  exports.TalkbackCancelInterceptor = TalkbackCancelInterceptor;
  exports.chain = chain;
  exports.collect = collect;
  exports.collectLatest = collectLatest;
  exports.concat = concat;
  exports.create = create;
  exports.emitter = emitter;
  exports.empty = empty;
  exports.filter = filter;
  exports.fromArray = fromArray;
  exports.fromObject = fromObject;
  exports.fromPromise = fromPromise;
  exports.join = join;
  exports.last = last;
  exports.listen = listen;
  exports.map = map;
  exports.merge = merge;
  exports.mergeLatest = mergeLatest;
  exports.multicast = multicast;
  exports.never = never;
  exports.of = of;
  exports.pipe = pipe;
  exports.reject = fail;
  exports.scan = scan;
  exports.skip = skip;
  exports.skipWhile = skipWhile;
  exports.slice = slice;
  exports.subscribe = subscribe;
  exports.switchMap = switchMap;
  exports.take = take;
  exports.takeWhile = takeWhile;
  exports.tap = tap;
  exports.throttle = throttle;

  Object.defineProperty(exports, '__esModule', { value: true });

})));
